import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { useJsApiLoader, GoogleMap, MarkerF } from '@react-google-maps/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { BRAND } from '@/config/brand';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const MAP_STYLE = { width: '100%', height: '100%' };

function OfficeMap({ lat, lng }) {
  const hasKey = Boolean(BRAND.googleMapsApiKey);
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: BRAND.googleMapsApiKey || '',
    id: 'casahom-gmaps',
  });

  const center = useMemo(() => ({ lat, lng }), [lat, lng]);
  const options = useMemo(
    () => ({ mapTypeControl: false, streetViewControl: false, fullscreenControl: false }),
    [],
  );

  if (!hasKey || loadError) {
    return (
      <iframe
        title={`${BRAND.name} office map`}
        className="h-full w-full border-0"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.02}%2C${lat - 0.015}%2C${lng + 0.02}%2C${lat + 0.015}&layer=mapnik&marker=${lat}%2C${lng}`}
      />
    );
  }

  if (!isLoaded) {
    return <div className="h-full w-full animate-pulse bg-slate-100" aria-hidden />;
  }

  return (
    <GoogleMap mapContainerStyle={MAP_STYLE} center={center} zoom={14} options={options}>
      <MarkerF position={center} />
    </GoogleMap>
  );
}

export default function Contact() {
  const revealRef = useScrollReveal();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState('');
  const { address } = BRAND;
  const fullAddress = `${address.line1}, ${address.city}, ${address.state} ${address.zip}`;

  const submit = (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    const next = {};
    if (!name) next.name = 'Name is required';
    if (!email) next.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Enter a valid email';
    if (!message || message.length < 10) next.message = 'Please enter at least 10 characters';
    setErrors(next);
    if (Object.keys(next).length) return;
    const phoneHint = BRAND.support.phone ? ` or call ${BRAND.support.phone}` : '';
    const msg = `Online contact form is temporarily unavailable. Please email us at ${BRAND.support.email}${phoneHint}.`;
    setNotice(msg);
    toast.error(msg, { duration: 8000 });
  };

  return (
    <div ref={revealRef}>
      <section className="relative hero-gradient noise-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <p className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-white/80">{BRAND.name}</p>
          <h1 className="font-display mx-auto mt-4 max-w-3xl text-4xl font-bold text-white sm:text-5xl">Contact us</h1>
          <p className="mx-auto mt-4 max-w-2xl text-white/85">
            Questions about listings, your account, or accessibility? We are here to help.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="reveal grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-semibold text-slate-900">{BRAND.legalName || BRAND.name}</h2>
            <ul className="mt-6 space-y-4 text-sm text-slate-700">
              <li className="flex gap-3">
                <MapPin className="mt-0.5 shrink-0 text-blue-600" size={18} aria-hidden />
                <span>
                  {address.line1}<br />
                  {address.city}, {address.state} {address.zip}
                </span>
              </li>
              <li className="flex gap-3">
                <Mail className="mt-0.5 shrink-0 text-blue-600" size={18} aria-hidden />
                <a href={`mailto:${BRAND.support.email}`} className="hover:text-blue-700">{BRAND.support.email}</a>
              </li>
              {BRAND.support.phone ? (
                <li className="flex gap-3">
                  <Phone className="mt-0.5 shrink-0 text-blue-600" size={18} aria-hidden />
                  <a href={`tel:${BRAND.support.phone.replace(/\D/g, '')}`} className="hover:text-blue-700">{BRAND.support.phone}</a>
                </li>
              ) : null}
              <li className="flex gap-3">
                <Clock className="mt-0.5 shrink-0 text-blue-600" size={18} aria-hidden />
                <span>{BRAND.hours}</span>
              </li>
            </ul>
            <p className="mt-6 text-sm text-slate-600">
              Looking for quick answers? Visit our{' '}
              <Link to="/help" className="text-blue-700 underline hover:text-blue-800">Help Center</Link>.
            </p>
            <div className="mt-8 h-72 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 sm:h-80">
              <OfficeMap lat={address.lat} lng={address.lng} />
            </div>
            <p className="mt-2 text-xs text-slate-400">{fullAddress}</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="font-display text-2xl font-semibold text-slate-900">Send a message</h2>
            <p className="mt-1 text-slate-600">We typically respond within one business day.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
              {notice && (
                <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
                  {notice}
                </p>
              )}
              <div>
                <Label htmlFor="contact-name">Name</Label>
                <Input
                  id="contact-name"
                  name="name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="mt-1.5 rounded-xl"
                  autoComplete="name"
                  aria-invalid={!!errors.name}
                />
                {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
              </div>
              <div>
                <Label htmlFor="contact-email">Email</Label>
                <Input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="mt-1.5 rounded-xl"
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="contact-message">Message</Label>
                <Textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  className="mt-1.5 rounded-xl"
                  aria-invalid={!!errors.message}
                />
                {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
              </div>
              <Button type="submit" className="rounded-xl">Send message</Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
